import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { isEmpty } from "../../../utils/Util";
import React, { useEffect, useState, useRef } from "react";
import { Drawer } from "rsuite";
import { TargetPayApi } from "../../../utils/UtilTarget";
import { StyleCreator } from "../../../helpers/stylecreator";
import { FormStyleCollection as formStyles } from "../../../styles/form";
import { toast } from "react-toastify";
import { IDealerLocationGroup } from "../../../types/types";
import { HttpStatusCode } from "axios";

export default function DealerLocationGroup({
  open,
  onClose,
  onSaveDealerLocationGroup,
  dmsDealerId,
  locationGroupData
}) {
  const classes = StyleCreator.makeStyles(formStyles.all);

  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [locationGroup, setLocationGroup] = useState(locationGroupData);

  const isEditMode = !!locationGroupData?.locationGroupId;
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocationGroup(locationGroupData);
    if (open && firstFieldRef.current) {
      setTimeout(() => {
        firstFieldRef.current?.focus();
      }, 200);
    }
  }, [locationGroupData, open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Clear previous error messages on input change
    setNameError("");
    setDescriptionError("");

    // Restrict only:
    // 1. Single double quote (")
    // 2. Two single-quotes typed together ('')
    const hasDoubleQuote = value.includes('"');
    const hasDoubleSingleQuotes = value.includes("''");

    if (hasDoubleQuote || hasDoubleSingleQuotes) {
      const errorMessage = `Double quotes (") and double single-quotes ('') are not allowed.`;

      // Set field-specific error
      if (name === "name") {
        setNameError(errorMessage);
      } else if (name === "description") {
        setDescriptionError(errorMessage);
      }

      // Stop updating state if input is invalid
      return;
    }

    // Update the LocationGroup state only if input is valid
    setLocationGroup(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const manageDealerLocationGroup = async () => {
    setNameError("");

    const trimmedName = locationGroup.name?.trim() || "";
    const trimmedDescription = locationGroup.description?.trim() || "";

    if (!trimmedName) {
      setNameError("Missing Name");
      return;
    }

    // Check if name or description changed (ignore spaces)
    const hasChanges =
      trimmedName !== locationGroupData?.name ||
      trimmedDescription !== locationGroupData?.description;

    if (isEditMode && !hasChanges) {
      toast.info("No changes detected.");
      onClose(null);
      return;
    }

    const payload: IDealerLocationGroup = {
      locationGroupId: locationGroup.locationGroupId,
      name: locationGroup.name,
      description: locationGroup.description,
      dmsDealerId: dmsDealerId,
      companyDealerId: locationGroup.companyDealerId
    };

    setIsProcessing(true);

    try {
      const response = await TargetPayApi(
        "ManageDealerLocationGroup",
        payload,
        "post",
        "Dealer",
        true,
        false
      );

      if (response > 0) {
        // Success handling
        if (!isEditMode) payload.locationGroupId = response;
        onSaveDealerLocationGroup(payload, isEditMode);

        toast.success(
          isEditMode
            ? "Location Group updated successfully."
            : "Location Group created successfully.",
          { position: toast.POSITION.TOP_CENTER, style: { width: "350px" } }
        );
      } else if (response?.response?.status === HttpStatusCode.BadRequest) {
        // Backend validation errors
        const errors = response.response.data.errors;
        Object.values(errors)
          .flat()
          .forEach(message =>
            toast.error(message as string, { autoClose: 5000 })
          );
        return;
      } else {
        toast.error("Something went wrong while creating the group.", {
          autoClose: 5000
        });
      }
    } catch (error) {
      const errorMsg = error.reasonPhrase || "An unexpected error occurred.";

      toast.error(errorMsg, {
        position: toast.POSITION.TOP_CENTER,
        style: { width: "470px" }
      });

      if (error?.status === HttpStatusCode.BadRequest && error.data?.errors) {
        Object.values(error.data.errors)
          .flat()
          .forEach(message => toast.error(`${message}`, { autoClose: 5000 }));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Determines whether the Create/Update button should be disabled
  const isButtonDisabled = () => {
    // Trimmed values for clean comparison
    const nameTrimmed = locationGroup.name.trim();
    const descTrimmed = locationGroup.description.trim();
    const originalName = locationGroupData?.name.trim();
    const originalDesc = locationGroupData?.description.trim();

    // In edit mode: check if name and description are unchanged
    const isUnchanged =
      isEditMode &&
      nameTrimmed === originalName &&
      descTrimmed === originalDesc;

    // Disable button when:
    // 1. API call is processing
    // 2. Name field is empty
    // 3. Name field has validation errors
    // 4. Description field has validation errors
    // 5. No changes were made in edit mode
    return (
      isProcessing ||
      !nameTrimmed ||
      !!nameError ||
      !!descriptionError ||
      isUnchanged
    );
  };

  return (
    <Drawer open={open} onClose={onClose} size="xs" enforceFocus={false}>
      <Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ padding: "0px", paddingRight: "0px", marginTop: "75px" }}
        ></Box>
        <Box
          style={{
            padding: "5px",
            fontSize: "18px",
            color: "#3366ff",
            backgroundColor: "",
            borderBottom: "1px solid #EAEDED"
          }}
        >
          {isEditMode ? "Edit Location Group" : "Create Location Group"}
        </Box>

        <Box
          style={{
            margin: "20px 5px 0px 15px"
          }}
        >
          <Box style={{ fontSize: "small" }}>
            <Box style={formStyles.fieldSpacer}>
              <TextField
                inputRef={firstFieldRef}
                label="Name"
                className={classes.root}
                variant="standard"
                fullWidth
                required
                id="outlined-required"
                name="name"
                value={locationGroup.name}
                inputProps={{
                  style: { fontSize: "small" },
                  maxLength: 100
                }}
                onChange={handleChange}
                onBlur={() => setNameError("")}
              />
            </Box>

            {nameError && <Box style={{ color: "red" }}>{nameError}</Box>}

            <Box style={formStyles.fieldSpacer}>
              <TextField
                label="Description"
                className={classes.root}
                variant="standard"
                fullWidth
                id="outlined-required"
                name="description"
                value={locationGroup.description}
                inputProps={{
                  style: { fontSize: "small" },
                  maxLength: 500
                }}
                multiline
                rows={3}
                onChange={handleChange}
                onBlur={() => setDescriptionError("")}
              />
            </Box>

            {descriptionError && (
              <Box style={{ color: "red" }}>{descriptionError}</Box>
            )}

            <Box style={{ padding: "10px 0" }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  disabled={isButtonDisabled()}
                  style={formStyles.fieldSpacer}
                  variant="contained"
                  onClick={manageDealerLocationGroup}
                >
                  {isEditMode ? "Update" : "Create"}
                </Button>

                {isProcessing && (
                  <CircularProgress
                    size={25}
                    sx={{ marginTop: "20px" }}
                    color="info"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
